import { useMemo, useState } from "react";
import clsx from "clsx";
import type { DayView, POI, Tag, ZonedTimeString } from "@poi-planner/shared";
import { useTripStore } from "../../store/tripStore";
import { useLibraryStore } from "../../store/libraryStore";
import { useUIStore } from "../../store/uiStore";
import { computeDayViews } from "../../utils/dayViews";
import {
  addMinutes,
  epochMs,
  minutesBetween,
  withTimeOfDay,
  zonedDate,
  zonedTimeOfDay,
} from "../../utils/zonedTime";
import { MIN_SPAN_MINUTES } from "../../utils/timeGrid";
import { checkConstraintViolations } from "../../utils/constraints";
import { formatTimeBudget } from "../../utils/format";
import { Button } from "../Button";
import { TagIcon } from "../TagIcon";
import { buildAccommodationContext } from "./accommodationContext";
import { AccommodationBanner } from "./AccommodationBanner";
import type { DayAccommodationContext } from "./types";

function formatAgendaDate(date: string): string {
  return new Date(date + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}

/** Short label for a zone badge, e.g. "Asia/Tokyo" → "TOKYO" */
function zoneBadgeLabel(zone: string): string {
  const city = zone.split("/").pop() ?? zone;
  return city.replace(/_/g, " ").toUpperCase();
}

interface TimeCommit {
  startTime: ZonedTimeString;
  endTime: ZonedTimeString;
}

/**
 * Inline-editable time range for an agenda row. Click swaps the readout for
 * start/end time inputs (wall clock in the block's own zone, date preserved);
 * Enter or focus-out commits, Escape cancels. Locked rows surface the locked
 * prompt instead of editing.
 */
function AgendaTimeRange({
  startTime,
  endTime,
  locked,
  isReadOnly,
  darkTone,
  onCommit,
}: {
  startTime: ZonedTimeString;
  endTime: ZonedTimeString;
  locked: boolean;
  isReadOnly: boolean;
  darkTone: boolean;
  onCommit: (times: TimeCommit) => void;
}) {
  const showLockedActionPrompt = useUIStore((s) => s.showLockedActionPrompt);
  const [editing, setEditing] = useState<{ start: string; end: string } | null>(
    null,
  );

  const beginEdit = () => {
    if (isReadOnly) return;
    if (locked) {
      showLockedActionPrompt("Unlock this item before changing its time.");
      return;
    }
    setEditing({
      start: zonedTimeOfDay(startTime),
      end: zonedTimeOfDay(endTime),
    });
  };

  const commit = () => {
    if (!editing) return;
    setEditing(null);
    if (!editing.start || !editing.end) return;

    const newStart = withTimeOfDay(startTime, editing.start);
    let newEnd = withTimeOfDay(endTime, editing.end);
    if (epochMs(newEnd) <= epochMs(newStart)) {
      newEnd = addMinutes(newStart, MIN_SPAN_MINUTES);
    }
    if (newStart === startTime && newEnd === endTime) return;
    onCommit({ startTime: newStart, endTime: newEnd });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") commit();
    if (e.key === "Escape") setEditing(null);
  };

  if (editing) {
    return (
      <div
        className="flex items-center gap-1"
        onBlur={(e) => {
          // Commit once focus leaves both inputs
          if (!e.currentTarget.contains(e.relatedTarget as Node)) commit();
        }}
      >
        <input
          type="time"
          className="input-base w-24 px-1.5 py-0.5 text-xs tabular-nums"
          value={editing.start}
          onChange={(e) =>
            setEditing((prev) => prev && { ...prev, start: e.target.value })
          }
          onKeyDown={handleKeyDown}
          aria-label="Start time"
          autoFocus
        />
        <span className="text-xs text-tertiary">–</span>
        <input
          type="time"
          className="input-base w-24 px-1.5 py-0.5 text-xs tabular-nums"
          value={editing.end}
          onChange={(e) =>
            setEditing((prev) => prev && { ...prev, end: e.target.value })
          }
          onKeyDown={handleKeyDown}
          aria-label="End time"
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={beginEdit}
      disabled={isReadOnly}
      className={clsx(
        "rounded px-1 py-0.5 text-xs font-bold tabular-nums transition-colors",
        darkTone ? "text-slate-300" : "text-secondary",
        !isReadOnly &&
          (darkTone
            ? "cursor-pointer hover:text-white"
            : "cursor-pointer hover:text-info"),
      )}
      title={isReadOnly ? undefined : "Edit times"}
    >
      {zonedTimeOfDay(startTime)} – {zonedTimeOfDay(endTime)}
    </button>
  );
}

/**
 * Chronological agenda: every scheduled block (POI visits + notes) grouped by
 * trip date and sorted by start instant, with inline time editing. Empty days
 * collapse to compact headers.
 */
export function CalendarAgendaView({
  dayViews: selectedDayViews,
}: {
  dayViews?: DayView[];
} = {}) {
  const trip = useTripStore((s) => s.trip);
  const schedule = useTripStore((s) => s.schedule);
  const isReadOnly = useTripStore((s) => s.isReadOnly);
  const updateScheduledPoi = useTripStore((s) => s.updateScheduledPoi);
  const updateNote = useTripStore((s) => s.updateNote);
  const pois = useLibraryStore((s) => s.pois);
  const tags = useLibraryStore((s) => s.tags);
  const getAccommodation = useLibraryStore((s) => s.getAccommodation);
  const openScheduledVisitEditor = useUIStore(
    (s) => s.openScheduledVisitEditor,
  );
  const openNoteEditor = useUIStore((s) => s.openNoteEditor);

  const allDayViews = useMemo(
    () => (trip ? computeDayViews(trip, schedule) : []),
    [trip, schedule],
  );
  const dayViews = selectedDayViews ?? allDayViews;
  const poiMap = useMemo(
    () => new Map<string, POI>(pois.map((poi) => [poi.id, poi])),
    [pois],
  );
  const tagMap = useMemo(
    () => new Map<string, Tag>(tags.map((tag) => [tag.id, tag])),
    [tags],
  );
  const accommodationByDate = useMemo(() => {
    const contexts = new Map<string, DayAccommodationContext>();
    for (const dayView of dayViews) {
      contexts.set(
        dayView.date,
        buildAccommodationContext(dayView.date, schedule, getAccommodation),
      );
    }
    return contexts;
  }, [dayViews, schedule, getAccommodation]);

  if (!trip) return null;

  const readOnly = isReadOnly();

  return (
    <div
      className="custom-scrollbar flex-1 overflow-y-auto bg-page"
      data-testid="calendar-agenda-view"
    >
      <div className="mx-auto max-w-2xl px-6 py-5">
        {dayViews.map((dayView, index) => {
          const agendaBlocks = dayView.blocks.filter(
            (block) => block.segment.isStart,
          );
          const allDayBlocks = dayView.allDayBlocks.filter(
            (block) => block.segment.isStart,
          );
          const accommodation = accommodationByDate.get(dayView.date)!;
          const hasAccommodation = !!accommodation.sleepPoi;
          const hasBlocks = agendaBlocks.length > 0 || allDayBlocks.length > 0;
          const isEmpty = !hasBlocks && !hasAccommodation;
          return (
            <section key={dayView.date} className={isEmpty ? "mb-2" : "mb-5"}>
              {/* Date header — compact when the day has no blocks */}
              <div
                className={clsx(
                  "flex items-baseline gap-2",
                  isEmpty ? "py-0.5 opacity-60" : "pb-1.5",
                )}
              >
                <span className="text-sm font-extrabold tracking-tight">
                  {formatAgendaDate(dayView.date)}
                </span>
                <span className="text-xs font-semibold text-secondary">
                  Day {index + 1}
                </span>
                {dayView.zone !== trip.defaultTimezone && (
                  <span
                    className="rounded bg-purple-100 px-1 py-px text-2xs font-extrabold tracking-wider text-purple-700"
                    title={`This day runs on ${dayView.zone} (trip default: ${trip.defaultTimezone})`}
                  >
                    {zoneBadgeLabel(dayView.zone)}
                  </span>
                )}
              </div>

              {!isEmpty && hasBlocks && (
                <ul className="flex flex-col gap-1.5">
                  {allDayBlocks.map(({ note }) => (
                    <li
                      key={note.id}
                      className="group flex items-center gap-3 rounded-xl border border-calendar-all-day-border bg-calendar-all-day-bg px-3 py-2 text-calendar-all-day-text shadow-sm"
                    >
                      <span className="flex items-center gap-1 rounded bg-calendar-all-day-bg-strong px-1.5 py-0.5 text-2xs font-extrabold uppercase tracking-wider">
                        <span
                          className="material-symbols-outlined text-sm"
                          aria-hidden
                        >
                          wb_sunny
                        </span>
                        All day
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-xs font-extrabold">
                          {note.name}
                        </div>
                        {note.allDayEndDate !== note.allDayStartDate && (
                          <div className="text-2xs font-semibold opacity-75">
                            {note.allDayStartDate} – {note.allDayEndDate}
                          </div>
                        )}
                      </div>
                      {note.locked && (
                        <span
                          className="material-symbols-outlined text-sm"
                          title="Dates locked"
                        >
                          lock
                        </span>
                      )}
                      {!readOnly && (
                        <span className="reveal-on-card-hover">
                          <Button
                            variant="icon"
                            size="sm"
                            icon="edit"
                            aria-label={`Edit all-day note ${note.name}`}
                            title="Edit note"
                            onClick={() => openNoteEditor("edit", note.id)}
                          />
                        </span>
                      )}
                    </li>
                  ))}
                  {agendaBlocks.map((block) => {
                    if (block.type === "poi") {
                      const sp = block.scheduledPOI;
                      const poi = poiMap.get(sp.poiId);
                      if (!poi) return null;
                      const primaryTag = poi.primaryTagId
                        ? tagMap.get(poi.primaryTagId)
                        : undefined;
                      const violations = checkConstraintViolations(
                        poi,
                        dayView.date,
                        zonedTimeOfDay(sp.startTime),
                      );
                      const span = Math.max(
                        Math.round(minutesBetween(sp.startTime, sp.endTime)),
                        0,
                      );

                      return (
                        <li
                          key={block.id}
                          role="button"
                          tabIndex={0}
                          onClick={(event) => {
                            if (
                              (event.target as HTMLElement).closest(
                                "button, input, a, select, textarea",
                              )
                            )
                              return;
                            openScheduledVisitEditor(sp.id);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              openScheduledVisitEditor(sp.id);
                            }
                          }}
                          className="group flex cursor-pointer items-center gap-3 rounded-xl border border-border bg-surface px-3 py-2 shadow-sm transition-colors hover:bg-sky-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-info"
                        >
                          <AgendaTimeRange
                            startTime={sp.startTime}
                            endTime={sp.endTime}
                            locked={sp.locked}
                            isReadOnly={readOnly}
                            darkTone={false}
                            onCommit={(times) =>
                              updateScheduledPoi(sp.id, times)
                            }
                          />
                          <div className="flex min-w-0 flex-1 items-center gap-1.5">
                            {primaryTag && (
                              <TagIcon icon={primaryTag.icon} size={13} />
                            )}
                            <span className="truncate text-xs font-bold tracking-tight">
                              {poi.name}
                            </span>
                            <span className="whitespace-nowrap text-2xs font-semibold text-tertiary">
                              {formatTimeBudget(span)}
                            </span>
                            {block.segment.count > 1 && (
                              <span className="whitespace-nowrap text-2xs font-bold text-info">
                                {zonedDate(sp.startTime)} →{" "}
                                {zonedDate(sp.endTime)}
                              </span>
                            )}
                          </div>
                          {violations.length > 0 && (
                            <span
                              className="material-symbols-outlined text-sm leading-none text-warning"
                              title={violations
                                .map((violation) => violation.message)
                                .join("\n")}
                            >
                              warning
                            </span>
                          )}
                          {sp.locked && (
                            <span
                              className="material-symbols-outlined text-sm leading-none text-tertiary"
                              title="Time locked"
                            >
                              lock
                            </span>
                          )}
                          <span className="reveal-on-card-hover">
                            <Button
                              variant="icon"
                              size="sm"
                              icon={readOnly ? "visibility" : "edit"}
                              aria-label={`${readOnly ? "View" : "Edit"} scheduled visit to ${poi.name}`}
                              title={readOnly ? "View visit" : "Edit visit"}
                              onClick={() => openScheduledVisitEditor(sp.id)}
                            />
                          </span>
                        </li>
                      );
                    }

                    const note = block.note;
                    const noteTags = note.tagIds
                      .map((tagId) => tagMap.get(tagId))
                      .filter((tag): tag is Tag => !!tag);
                    const startPoi = note.startPoiId
                      ? poiMap.get(note.startPoiId)
                      : undefined;
                    const endPoi = note.endPoiId
                      ? poiMap.get(note.endPoiId)
                      : undefined;
                    const routeSubtitle =
                      startPoi || endPoi
                        ? `${startPoi?.name ?? ""} → ${endPoi?.name ?? ""}`.trim()
                        : null;

                    return (
                      <li
                        key={block.id}
                        className="group flex items-center gap-3 rounded-xl bg-slate-800 px-3 py-2 text-slate-200 shadow-sm"
                        style={{
                          borderLeft: "4px solid var(--color-sky-500)",
                        }}
                      >
                        <AgendaTimeRange
                          startTime={note.startTime}
                          endTime={note.endTime}
                          locked={note.locked}
                          isReadOnly={readOnly}
                          darkTone
                          onCommit={(times) => updateNote(note.id, times)}
                        />
                        <div className="flex min-w-0 flex-1 items-center gap-1.5">
                          {noteTags[0] && (
                            <TagIcon
                              icon={noteTags[0].icon}
                              size={13}
                              whiteTint
                            />
                          )}
                          <span className="truncate text-xs font-bold tracking-tight text-slate-50">
                            {note.name}
                          </span>
                          {routeSubtitle && (
                            <span className="truncate text-2xs font-semibold text-slate-400">
                              {routeSubtitle}
                            </span>
                          )}
                          {block.segment.count > 1 && (
                            <span className="whitespace-nowrap text-2xs font-bold text-sky-300">
                              {zonedDate(note.startTime)} →{" "}
                              {zonedDate(note.endTime)}
                            </span>
                          )}
                        </div>
                        {note.locked && (
                          <span
                            className="material-symbols-outlined text-sm leading-none text-slate-400"
                            title="Time locked"
                          >
                            lock
                          </span>
                        )}
                        {!readOnly && (
                          <span className="reveal-on-card-hover">
                            <Button
                              variant="icon"
                              size="sm"
                              icon="edit"
                              aria-label={`Edit note ${note.name}`}
                              title="Edit note"
                              onClick={() => openNoteEditor("edit", note.id)}
                            />
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}

              {!isEmpty && hasAccommodation && (
                <div className={hasBlocks ? "mt-1.5" : undefined}>
                  <AccommodationBanner
                    date={dayView.date}
                    accommodation={accommodation}
                    isReadOnly={readOnly}
                  />
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
